import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Laptop, Video, Clock, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import useSEO from '../hooks/useSEO';
import { conditions, clinicInfo } from '../data/mockData';

const TreatmentsPage = () => {
  useSEO(
    'Physiotherapy Services in Bellandur, Bangalore | Mind Motion',
    'Expert physiotherapy services for everyday pain, sports injuries, post-surgical rehabilitation and more in Bellandur, Bangalore.'
  );

  const categories = conditions.reduce((acc, condition) => {
    const key = condition.category || 'other';
    const title = condition.categoryName || (key.charAt(0).toUpperCase() + key.slice(1));

    if (!acc[key]) {
      acc[key] = { title, items: [] };
    }

    acc[key].items.push(condition);
    return acc;
  }, {});

  // Keep a deterministic display order when possible.
  const categoryOrder = ['everyday', 'sports', 'specific', 'orthopedic', 'spine', 'ergonomic'];
  const orderedCategories = Object.keys(categories)
    .sort((a, b) => {
      const aIndex = categoryOrder.indexOf(a);
      const bIndex = categoryOrder.indexOf(b);
      if (aIndex === -1 && bIndex === -1) return a.localeCompare(b);
      if (aIndex === -1) return 1;
      if (bIndex === -1) return -1;
      return aIndex - bIndex;
    })
    .map(key => ({ key, ...categories[key] }));

  return (
    <div className="min-h-screen bg-background">
      {/* Online Physiotherapy CTA Section */}
      <section className="py-12 bg-primary/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-primary/10">
            <div className="grid md:grid-cols-2 items-center">
              <div className="p-8 md:p-12">
                <span className="inline-block px-4 py-1.5 bg-secondary text-primary font-bold text-sm rounded-full mb-6">
                  Can't visit us in person?
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
                  Online Physiotherapy & Teleconsultation
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  Get expert care from the comfort of your home. Our virtual consultations provide 
                  the same evidence-led approach as our in-clinic visits, tailored for your recovery journey.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/online-physiotherapy">
                    <Button size="lg" className="w-full sm:w-auto px-8">
                      Learn More About Online Consult
                    </Button>
                  </Link>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    className="w-full sm:w-auto"
                    onClick={() => window.open(`https://wa.me/${clinicInfo.whatsapp.replace(/[^0-9]/g, '')}?text=Hi, I would like to book an online consultation`, '_blank')}
                  >
                    Book Teleconsult Now
                  </Button>
                </div>
              </div>
              <div className="h-64 md:h-full relative min-h-[300px]">
                <img 
                  src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&q=80" 
                  alt="Online Physiotherapy Consultation" 
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent md:bg-gradient-to-l" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section className="bg-gradient-to-b from-secondary to-background py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">
            Our Treatments
          </h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Specialized physiotherapy care for a wide range of musculoskeletal conditions.
            Evidence-based protocols designed for long-term relief and recovery.
          </p>
        </div>
      </section>

      {/* Treatments by Category */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {orderedCategories.map(({ key, title, items }) => (
            items.length > 0 && (
              <div key={key} className="mb-16">
                <h2 className="text-3xl font-bold text-primary mb-8">
                  {title}
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {items.map((condition) => (
                    <Link
                      key={condition.id}
                      to={`/treatment/${condition.id}`}
                      className="group"
                    >
                      <Card className="overflow-hidden border-2 border-gray-100 hover:border-primary transition-all duration-300 hover:shadow-lg h-full">
                        <div className="relative h-48 overflow-hidden">
                          <img
                            src={condition.image}
                            alt={condition.name}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <CardContent className="p-6">
                          <h3 className="text-xl font-bold text-primary mb-2 group-hover:text-primary/80 transition-colors">
                            {condition.name}
                          </h3>
                          {/* <p className="text-muted-foreground text-sm mb-4">{condition.shortDesc}</p> */}
                          <span className="text-primary font-medium text-sm inline-flex items-center">
                            Learn more <ArrowRight className="ml-1 w-4 h-4" />
                          </span>
                        </CardContent>
                      </Card>
                    </Link>
                  ))}
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-muted">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">
            Evidence-Led Treatment
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            Every treatment protocol at Mind Motion is based on the latest clinical research and proven rehabilitation
            techniques. We don't offer quick fixes – we provide comprehensive, personalized care that addresses the
            root cause of your condition for lasting relief.
          </p>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">85%</div>
              <p className="text-muted-foreground">Recovery rate within 8 weeks</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">1000+</div>
              <p className="text-muted-foreground">Inquiries in the past 1 year</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary mb-2">750+</div>
              <p className="text-muted-foreground">Successful discharges in the past 1 year</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TreatmentsPage;
